import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Form, Spinner } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import PrimaryButton from '../shared/PrimaryButton';
import OutlinePrimaryButton from '../shared/OutlinePrimaryButton';
import {
  useAccountTransfers,
  useCreateAccountTransfer,
  useDeleteAccountTransfer,
  useUpdateAccountTransfer,
} from '../../hooks/useAccountTransfers';
import { signedTransferAmount, transferDateKey } from '../../utils/transferAdjustedEquity';

function formatDateInput(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatAmount(value) {
  return `$${Math.abs(Number(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatRowDate(value) {
  const key = transferDateKey(value);
  if (!key) return '—';
  return new Date(`${key}T12:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Transfers for the selected account: review and edit recorded deposits/withdrawals,
 * or step into the log screen to add a new one.
 */
export default function AccountTransferModal({
  show,
  onHide,
  accountId,
  isPaperTrading = false,
  showMessage,
  onSuccess,
}) {
  const { colors } = useTheme();
  const createTransfer = useCreateAccountTransfer();
  const updateTransfer = useUpdateAccountTransfer();
  const deleteTransfer = useDeleteAccountTransfer();

  const {
    data: transfers = [],
    isLoading: transfersLoading,
  } = useAccountTransfers({
    accountId,
    paperTrading: isPaperTrading,
    enabled: !!show && !!accountId,
  });

  const [view, setView] = useState('list');

  const [direction, setDirection] = useState('in');
  const [amount, setAmount] = useState('');
  const [transferDate, setTransferDate] = useState(formatDateInput());
  const [note, setNote] = useState('');

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const resetForm = useCallback(() => {
    setDirection('in');
    setAmount('');
    setTransferDate(formatDateInput());
    setNote('');
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditDraft(null);
  }, []);

  useEffect(() => {
    if (!show) return;
    setView('list');
    resetForm();
    cancelEdit();
    setPendingDeleteId(null);
  }, [show, resetForm, cancelEdit]);

  const amountNumber = useMemo(() => {
    const n = Number(String(amount).replace(/,/g, ''));
    return Number.isFinite(n) ? n : NaN;
  }, [amount]);

  const canSubmit =
    !!accountId &&
    Number.isFinite(amountNumber) &&
    amountNumber > 0 &&
    !!transferDate &&
    !createTransfer.isPending;

  const sortedTransfers = useMemo(
    () =>
      [...transfers].sort((a, b) => {
        const aKey = transferDateKey(a.transfer_date) || '';
        const bKey = transferDateKey(b.transfer_date) || '';
        if (aKey === bKey) return (b.id ?? 0) - (a.id ?? 0);
        return aKey < bKey ? 1 : -1;
      }),
    [transfers]
  );

  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault?.();
      if (!canSubmit) return;

      try {
        await createTransfer.mutateAsync({
          account_id: String(accountId),
          amount: amountNumber,
          direction,
          transfer_date: transferDate,
          is_paper_trading: !!isPaperTrading,
          note: note.trim() || null,
        });
        showMessage?.(direction === 'out' ? 'Withdrawal recorded' : 'Deposit recorded', 'success');
        onSuccess?.();
        resetForm();
        setView('list');
      } catch (err) {
        showMessage?.(err?.message || 'Failed to save transfer', 'danger');
      }
    },
    [
      accountId,
      amountNumber,
      canSubmit,
      createTransfer,
      direction,
      isPaperTrading,
      note,
      onSuccess,
      resetForm,
      showMessage,
      transferDate,
    ]
  );

  const startEdit = useCallback((transfer) => {
    const signed = signedTransferAmount(transfer);
    setEditingId(transfer.id);
    setEditDraft({
      direction: signed < 0 ? 'out' : 'in',
      amount: String(Math.abs(signed)),
      transferDate: transferDateKey(transfer.transfer_date) || formatDateInput(),
      note: transfer.note || '',
    });
    setPendingDeleteId(null);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingId || !editDraft) return;
    const editAmount = Number(String(editDraft.amount).replace(/,/g, ''));
    if (!Number.isFinite(editAmount) || editAmount <= 0 || !editDraft.transferDate) {
      showMessage?.('Enter an amount and date', 'danger');
      return;
    }

    try {
      await updateTransfer.mutateAsync({
        id: editingId,
        account_id: String(accountId),
        amount: editAmount,
        direction: editDraft.direction,
        transfer_date: editDraft.transferDate,
        is_paper_trading: !!isPaperTrading,
        note: editDraft.note.trim() || null,
      });
      showMessage?.('Transfer updated', 'success');
      cancelEdit();
      onSuccess?.();
    } catch (err) {
      showMessage?.(err?.message || 'Failed to update transfer', 'danger');
    }
  }, [
    accountId,
    cancelEdit,
    editDraft,
    editingId,
    isPaperTrading,
    onSuccess,
    showMessage,
    updateTransfer,
  ]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        await deleteTransfer.mutateAsync(id);
        showMessage?.('Transfer deleted', 'success');
        if (editingId === id) cancelEdit();
        onSuccess?.();
      } catch (err) {
        showMessage?.(err?.message || 'Failed to delete transfer', 'danger');
      } finally {
        setPendingDeleteId(null);
      }
    },
    [cancelEdit, deleteTransfer, editingId, onSuccess, showMessage]
  );

  const inputStyle = {
    background: colors.bgSecondary,
    color: colors.text,
    borderColor: colors.border,
  };

  const rowInputStyle = {
    ...inputStyle,
    fontSize: '0.8rem',
    padding: '4px 8px',
    height: 30,
  };

  const linkButtonStyle = {
    background: 'none',
    border: 'none',
    padding: '2px 4px',
    fontSize: '0.75rem',
    fontWeight: 600,
    cursor: 'pointer',
  };

  const renderEditRow = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <Form.Select
          value={editDraft.direction}
          onChange={(e) => setEditDraft((d) => ({ ...d, direction: e.target.value }))}
          style={{ ...rowInputStyle, width: 122, flexShrink: 0 }}
          aria-label="Direction"
          data-testid="transfer-edit-direction"
        >
          <option value="in">Deposit</option>
          <option value="out">Withdrawal</option>
        </Form.Select>
        <Form.Control
          type="number"
          min="0"
          step="0.01"
          value={editDraft.amount}
          onChange={(e) => setEditDraft((d) => ({ ...d, amount: e.target.value }))}
          style={{ ...rowInputStyle, width: 110, flexShrink: 0 }}
          aria-label="Amount"
          data-testid="transfer-edit-amount"
        />
        <Form.Control
          type="date"
          value={editDraft.transferDate}
          onChange={(e) => setEditDraft((d) => ({ ...d, transferDate: e.target.value }))}
          style={{ ...rowInputStyle, flex: 1, minWidth: 140 }}
          aria-label="Date"
          data-testid="transfer-edit-date"
        />
      </div>
      <Form.Control
        type="text"
        value={editDraft.note}
        onChange={(e) => setEditDraft((d) => ({ ...d, note: e.target.value }))}
        placeholder="Note (optional)"
        style={rowInputStyle}
        aria-label="Note"
        data-testid="transfer-edit-note"
      />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={cancelEdit}
          style={{ ...linkButtonStyle, color: colors.textMuted }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSaveEdit}
          disabled={updateTransfer.isPending}
          style={{ ...linkButtonStyle, color: colors.primary }}
          data-testid="transfer-edit-save"
        >
          {updateTransfer.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );

  const renderReadRow = (transfer) => {
    const signed = signedTransferAmount(transfer);
    const isDeposit = signed >= 0;
    const confirming = pendingDeleteId === transfer.id;

    return (
      <>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: '0.85rem', color: colors.text, fontWeight: 600 }}>
            {isDeposit ? '+' : '−'}
            {formatAmount(signed)}
            <span
              style={{
                marginLeft: 8,
                fontSize: '0.75rem',
                fontWeight: 500,
                color: colors.textMuted,
              }}
            >
              {isDeposit ? 'Deposit' : 'Withdrawal'}
            </span>
          </div>
          <div style={{ fontSize: '0.75rem', color: colors.textMuted }}>
            {formatRowDate(transfer.transfer_date)}
            {transfer.note ? ` · ${transfer.note}` : ''}
          </div>
        </div>

        {confirming ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              onClick={() => handleDelete(transfer.id)}
              disabled={deleteTransfer.isPending}
              style={{ ...linkButtonStyle, color: colors.chartDown }}
              data-testid="transfer-delete-confirm"
            >
              Confirm
            </button>
            <button
              type="button"
              onClick={() => setPendingDeleteId(null)}
              style={{ ...linkButtonStyle, color: colors.textMuted }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              onClick={() => startEdit(transfer)}
              style={{ ...linkButtonStyle, color: colors.primary }}
              data-testid="transfer-edit"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => setPendingDeleteId(transfer.id)}
              style={{ ...linkButtonStyle, color: colors.textMuted }}
              data-testid="transfer-delete"
            >
              Delete
            </button>
          </div>
        )}
      </>
    );
  };

  const listView = (
    <>
      <Modal.Body style={{ color: colors.text }}>
        <p style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: 16 }}>
          Deposits and withdrawals recorded for this account. P&amp;L% is adjusted from each
          transfer&apos;s date, so the date should match the day the cash hit the account.
        </p>

        {transfersLoading ? (
          <Spinner animation="border" size="sm" style={{ color: colors.textMuted }} />
        ) : !sortedTransfers.length ? (
          <div style={{ fontSize: '0.85rem', color: colors.textMuted }}>
            No transfers recorded yet. Use Log Transfer to add one.
          </div>
        ) : (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
            data-testid="transfer-list"
          >
            {sortedTransfers.map((transfer) => {
              const isEditing = editingId === transfer.id && !!editDraft;
              return (
                <div
                  key={transfer.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: `1px solid ${isEditing ? colors.primary : colors.border}`,
                    background: colors.bgSecondary,
                  }}
                  data-testid="transfer-row"
                >
                  {isEditing ? renderEditRow() : renderReadRow(transfer)}
                </div>
              );
            })}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer style={{ borderColor: colors.border, gap: 8 }}>
        <OutlinePrimaryButton type="button" size="sm" onClick={onHide}>
          Close
        </OutlinePrimaryButton>
        <PrimaryButton
          type="button"
          size="sm"
          variant="compact"
          onClick={() => {
            cancelEdit();
            setPendingDeleteId(null);
            resetForm();
            setView('form');
          }}
          data-testid="log-transfer-button"
        >
          Log Transfer
        </PrimaryButton>
      </Modal.Footer>
    </>
  );

  const formView = (
    <Form onSubmit={handleSubmit}>
      <Modal.Body style={{ color: colors.text }}>
        <p style={{ fontSize: '0.85rem', color: colors.textMuted, marginBottom: 16 }}>
          Record a deposit or withdrawal so P&amp;L% reflects trading performance, not cash moves.
          Use the date the cash actually hit the account — the adjustment starts on that day.
        </p>

        <Form.Group className="mb-3">
          <Form.Label style={{ fontSize: '0.8rem', color: colors.textMuted }}>Direction</Form.Label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { id: 'in', label: 'Deposit' },
              { id: 'out', label: 'Withdrawal' },
            ].map((opt) => {
              const active = direction === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setDirection(opt.id)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: 8,
                    border: `1px solid ${active ? colors.primary : colors.border}`,
                    background: active ? colors.primary : 'transparent',
                    color: active ? '#fff' : colors.text,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label style={{ fontSize: '0.8rem', color: colors.textMuted }}>Amount</Form.Label>
          <Form.Control
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="1000.00"
            style={inputStyle}
            required
            data-testid="transfer-amount-input"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label style={{ fontSize: '0.8rem', color: colors.textMuted }}>Date</Form.Label>
          <Form.Control
            type="date"
            value={transferDate}
            onChange={(e) => setTransferDate(e.target.value)}
            style={inputStyle}
            required
            data-testid="transfer-date-input"
          />
        </Form.Group>

        <Form.Group className="mb-0">
          <Form.Label style={{ fontSize: '0.8rem', color: colors.textMuted }}>
            Note <span style={{ fontWeight: 400 }}>(optional)</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Initial funding"
            style={inputStyle}
            data-testid="transfer-note-input"
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer style={{ borderColor: colors.border, gap: 8 }}>
        <OutlinePrimaryButton
          type="button"
          size="sm"
          onClick={() => setView('list')}
          disabled={createTransfer.isPending}
          data-testid="transfer-back-button"
        >
          Back
        </OutlinePrimaryButton>
        <PrimaryButton
          type="submit"
          size="sm"
          variant="compact"
          disabled={!canSubmit}
          loading={createTransfer.isPending}
          data-testid="transfer-submit-button"
        >
          {createTransfer.isPending ? (
            <>
              <Spinner animation="border" size="sm" style={{ marginRight: 6 }} />
              Saving…
            </>
          ) : (
            'Save Transfer'
          )}
        </PrimaryButton>
      </Modal.Footer>
    </Form>
  );

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="md"
      className="trade-risk-quick-setup-modal account-transfer-modal"
      dialogClassName="trade-risk-quick-setup-dialog"
      contentClassName="trade-risk-quick-setup-content"
    >
      <Modal.Header closeButton style={{ borderColor: colors.border }}>
        <Modal.Title style={{ color: colors.text, fontSize: '1.1rem' }}>
          {view === 'form' ? 'Log Transfer' : 'Transfers'}
        </Modal.Title>
      </Modal.Header>

      {view === 'form' ? formView : listView}
    </Modal>
  );
}
